import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type PropsWithChildren,
} from "react";

import { cn } from "@tudigo-monorepo/core-tudigo-theme";

import type { ComparatorScrollerProps } from "./comparator-scroller";
import { ComparatorScrollerButtons } from "./comparator-scroller-buttons";
import { useComparatorScroller } from "./store/hooks/use-comparator-scroller";

type BarState = "sticky" | "static";

interface ComparatorScrollerStickybarProps extends PropsWithChildren {
  scrollButtonsPositionType: ComparatorScrollerProps["scrollButtonsPositionType"];
  className?: string;
}

export function ComparatorScrollerStickybar({
  children,
  className,
  scrollButtonsPositionType,
}: ComparatorScrollerStickybarProps) {
  const {
    state: { scrollLeft, maxScrollWidth },
  } = useComparatorScroller();

  const barRef = useRef<HTMLDivElement | null>(null),
    initialPositionTopRef = useRef<number | null>(null);

  const [barState, setBarState] = useState<BarState>("static");

  const setBarRef = useCallback((element: HTMLDivElement) => {
    if (element) {
      barRef.current = element;
      const elementTop = element.getBoundingClientRect().top;
      const containerPaddingTop = window.innerWidth >= 1024 ? 32 : 0;
      initialPositionTopRef.current =
        Math.round(elementTop) - containerPaddingTop;
    }
  }, []);

  const updateBarStateOnModalScroll = useCallback(
    (e: any) => {
      if (!initialPositionTopRef?.current) return;

      let { scrollTop } = e.currentTarget?.closest(
        "[data-comparator-modal-content]",
      ) as HTMLElement;

      if (/iPhone/i.test(navigator.userAgent)) {
        const addressBarHeight = 326;
        scrollTop += addressBarHeight;
      }

      const isSticky = scrollTop >= initialPositionTopRef.current;

      setBarState(isSticky ? "sticky" : "static");
    },
    [initialPositionTopRef, setBarState],
  );

  useEffect(() => {
    const modalContent = barRef.current?.closest(
      "[data-comparator-modal-content]",
    );

    if (modalContent) {
      modalContent.addEventListener("scroll", updateBarStateOnModalScroll, {
        passive: true,
      });

      return () => {
        modalContent.removeEventListener("scroll", updateBarStateOnModalScroll);
      };
    }
  }, [updateBarStateOnModalScroll]);

  return (
    <div
      ref={setBarRef}
      data-state={barState}
      className={cn(
        "z-popover sticky left-0 top-0 -mt-px min-h-[154px] w-full lg:min-h-fit",
        "flex flex-col gap-y-4 py-4",
        "border-neutral-200 lg:border-b",
        "ease transition-colors",
        "data-[state=static]:bg-white",
        "data-[state=sticky]:bg-neutral-100",
        className,
      )}
    >
      <div
        data-state={barState}
        className={cn(
          "flex w-full items-center justify-between gap-y-4 self-start overflow-x-hidden px-4 lg:px-10",
          "data-[state=static]:flex-col",
          "data-[state=static]:lg:flex-row",
        )}
      >
        <h4 className="font-roobert text-black-primary text-lg font-semibold">
          Comparatif
        </h4>
        {scrollButtonsPositionType === "default" && (
          <ComparatorScrollerButtons />
        )}
      </div>

      <div
        data-state={barState}
        className="relative flex w-full items-center self-end data-[state=static]:hidden lg:mr-10"
      >
        <div
          data-state={barState}
          className="z-popover hidden h-[50px] w-auto bg-neutral-100 data-[state=sticky]:lg:block"
        />
        {!!children && (
          <div
            data-state={barState}
            className="no-scrollbar sticky right-0 h-[50px] w-full self-end px-4 will-change-transform lg:ml-auto lg:px-0"
            style={{
              width: maxScrollWidth,
              transform: `translate3d(-${scrollLeft}px,0,0)`,
            }}
          >
            {children}
          </div>
        )}
        <div
          data-state={barState}
          className="z-popover ease sticky right-0 -mr-10 hidden h-[50px] min-w-10 bg-neutral-100 transition-colors data-[state=sticky]:lg:block"
        />
      </div>
    </div>
  );
}
